import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { forwardRef, useImperativeHandle, useEffect } from "react";
import { FormField } from "@/components/ui/FormField";

export const mobileNumberSchema = z.object({
  Country_Code: z.string().min(2, "Country Code must be at least 2 characters"),
  Number: z
    .string()
    .min(10, "Number must be at least 10 characters")
    .regex(/^[6-9]\d{9}$/, "Number must start with 9, 8, 7, or 6 and be 10 digits"),
});

export type MobileNumberFormData = z.infer<typeof mobileNumberSchema>;

export interface AddMobileNumberHandle {
  submit: () => void;
  setFieldError: (field: string, message: string) => void;
  clearFieldErrors: () => void; reset?: () => void;
}

interface AddMobileNumberFormProps {
  onSubmit: (data: MobileNumberFormData) => void;
  defaultValues?: MobileNumberFormData;
  isLoading?: boolean;
}

const AddMobileNumberForm = forwardRef<AddMobileNumberHandle, AddMobileNumberFormProps>(
  ({ onSubmit, defaultValues, isLoading }, ref) => {
    const form = useForm();
        const {
            control,
            handleSubmit,
            formState: { errors },
            clearErrors,
            setError,
            reset,
        } = useForm<MobileNumberFormData>({
            resolver: zodResolver(mobileNumberSchema),
            defaultValues: defaultValues || {
                Country_Code: "",
                Number: "",
            },
        });

    useEffect(() => {
      if (defaultValues) {
        reset(defaultValues);
      }
    }, [defaultValues, reset]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)();
      },
      setFieldError: (field: string, message: string) => {
        setError(field as keyof MobileNumberFormData, { type: "server", message });
      },
      clearFieldErrors: () => clearErrors(),
      reset: () => {
      form.reset();
  }
    }));

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={control}
          label="Country Code"
          name="Country_Code"
          error={errors.Country_Code?.message}
          required
          placeholder="e.g. +91"
          disabled={isLoading}
          onClear={() => clearErrors("Country_Code")}
        />
        <FormField
          control={control}
          label="Number"
          name="Number"
          error={errors.Number?.message}
          required
          placeholder="e.g. 1234567890"
          disabled={isLoading}
          onClear={() => clearErrors("Number")}
          maxLength={10}
        />
      </form>
    );
  }
);

AddMobileNumberForm.displayName = "AddMobileNumberForm";

export default AddMobileNumberForm;
